<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useFormStore } from '@/stores/form';
import { useUserStore } from '@/stores/user';
import { cityZipCodes } from '@/data/cityZipcodes';

const formStore = useFormStore();
const userStore = useUserStore();

const RegAgent = ref('')
const RegiestAccount = ref('')
const RegiestName = ref('')
const RegiestCompanyNo = ref('')
const RegiestCellPhone = ref('')
const RegCity = ref('')
const RegAddress = ref('')
const RegZipCode = ref('')
const RegiestPw1 = ref('')
const RegiestPw2 = ref('')
const showPw1 = ref(false)
const showPw2 = ref(false)
const validationErrors = ref<string[]>([])
const isCheckingAccount = ref(false)
const nicknameError = ref('')
const accountError = ref('')

// 驗證：Email 格式
const isEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(RegiestAccount.value))

// 驗證：密碼一致
const isPasswordMatch = computed(() => RegiestPw1.value === RegiestPw2.value)

// 驗證：電話格式（行動電話：09開頭10碼；室內電話：區碼+8碼，共10碼）
const isPhoneValid = computed(() => {
  const mobileRegex = /^09\d{8}$/
  const landlineRegex = /^0[2-8]\d{8}$/
  return mobileRegex.test(RegiestCellPhone.value) || landlineRegex.test(RegiestCellPhone.value)
})

// 修正：動態生成所有地區選項
const sortedDistricts = computed(() => Object.keys(cityZipCodes))

// 驗證：暱稱不為空
const isNicknameValid = computed(() => RegiestName.value.trim() !== '')
// 驗證：城市不為空
const isCityValid = computed(() => RegCity.value.trim() !== '')
// 驗證：地址不為空
const isAddressValid = computed(() => RegAddress.value.trim() !== '')
// 驗證：密碼要求（至少8個字符，包含大小寫、數字和符號）
const isPasswordValid = computed(() => {
  const val = RegiestPw1.value
  return val.length >= 8 && /[A-Z]/.test(val) && /[a-z]/.test(val) && /[0-9]/.test(val) && /[\W_]/.test(val)
})

// 密碼強度
const passwordStrength = computed(() => {
  const val = RegiestPw1.value
  let score = 0
  if (val.length >= 8) score++
  if (/[A-Z]/.test(val)) score++
  if (/[a-z]/.test(val)) score++
  if (/[0-9]/.test(val)) score++
  if (/[\W_]/.test(val)) score++

  if (score <= 2) return { label: '必須要有英文大小寫數字符號', color: 'red' }
  if (score <= 4) return { label: '必須要有英文大小寫數字符號', color: 'orange' }
  return { label: '合乎規範', color: 'lightgreen' }
})

// 根據城市自動填入郵遞區號
const onCityChange = () => {
  RegZipCode.value = cityZipCodes[RegCity.value] || ''
}

// 驗證暱稱
const validateNickname = () => {
  nicknameError.value = RegiestName.value.trim() === '' ? '暱稱為必填欄位' : ''
}

// 驗證帳號
const validateAccount = () => {
  accountError.value = RegiestAccount.value && !isEmailValid.value ? '請輸入正確的電子郵件格式' : ''
}

// 檢查所有驗證並生成錯誤訊息
const validateForm = () => {
  validationErrors.value = []

  if (!isNicknameValid.value) {
    validationErrors.value.push('暱稱為必填欄位，請務必填寫。')
  }
  if (!isEmailValid.value && RegiestAccount.value) {
    validationErrors.value.push('請輸入正確的電子郵件格式。')
  }
  if (!isPhoneValid.value && RegiestCellPhone.value) {
    validationErrors.value.push('請輸入正確的電話號碼（行動電話09開頭10碼，或室內電話如02開頭10碼）。')
  }
  if (!isCityValid.value) {
    validationErrors.value.push('請選擇縣/市。')
  }
  if (!isAddressValid.value) {
    validationErrors.value.push('請輸入收件地址。')
  }
  if (!isPasswordValid.value && RegiestPw1.value) {
    validationErrors.value.push('密碼必須包含至少8個字符，包括大小寫字母、數字和符號。')
  }
  if (!isPasswordMatch.value && RegiestPw2.value) {
    validationErrors.value.push('密碼不一致。')
  }
}

// 註冊按鈕啟用條件
const RegBtnenable = computed(() =>
  userStore.isAccOk &&
  isEmailValid.value &&
  isPhoneValid.value &&
  isPasswordMatch.value &&
  isPasswordValid.value &&
  isNicknameValid.value &&
  isCityValid.value &&
  isAddressValid.value
)

// 切換頁面
const showLogin = () => {
  formStore.switchForm('login')
}

const showPay = () => {
  formStore.switchForm('pay')
}

// 檢查帳號
const doCheckAcc = async () => {
  validateAccount()
  if (!RegiestAccount.value || !isEmailValid.value) {
    userStore.isAccOk = false
    userStore.errorMessageRegiedt = RegiestAccount.value ? '請輸入正確的電子郵件格式' : ''
    return
  }

  isCheckingAccount.value = true
  try {
    await userStore.CheckAccount(RegiestAccount.value);
    if (userStore.isAccOk) {
      userStore.errorMessageRegiedt = '帳號可用'
      accountError.value = ''
    }
  } catch (error) {
    userStore.isAccOk = false
    userStore.errorMessageRegiedt = '無法檢查帳號，請稍後再試'
  } finally {
    isCheckingAccount.value = false
  }
}

// 執行註冊
const DoRegiest = async () => {

  validateForm()
  if (validationErrors.value.length > 0) {
    return
  }

  const success = await userStore.UserAdd(
    RegAgent.value,
    RegiestName.value,
    RegiestCompanyNo.value,
    RegiestCellPhone.value,
    RegiestAccount.value,
    RegiestPw1.value,
    RegCity.value,
    RegZipCode.value,
    RegAddress.value
  )

  if (success) {
    // Clear form fields
    RegiestName.value = RegiestCellPhone.value = RegiestAccount.value = RegiestPw1.value = RegiestPw2.value = RegiestCompanyNo.value = ''
    RegCity.value = RegAddress.value = RegZipCode.value = ''
    userStore.isAccOk = false
    userStore.errorMessageRegiedt = ''
    nicknameError.value = ''
    accountError.value = ''
    alert('注意 !!!  請注意信箱＜垃圾箱檢查＞，認證信件啟用後可以登入系統 請先點選儲值。')
    formStore.switchForm('login')
  } else {
    validationErrors.value.push(userStore.errorMessageRegiedt || '註冊失敗，請稍後再試')
  }
}

// Clear error message when typing new email
watch(RegiestAccount, () => {
  userStore.errorMessageRegiedt = ''
  userStore.isAccOk = false
  accountError.value = ''
})

// Clear nickname error when typing
watch(RegiestName, () => {
  nicknameError.value = ''
})

onMounted(() => {
  RegAgent.value = userStore.agentInfo?.name || '虔智科技';
})
</script>

<template>
  <h1>註冊帳號</h1>
  <div class="dRegiest">
    <div v-if="validationErrors.length > 0" class="error-summary">
      <p v-for="(error, index) in validationErrors" :key="index" class="field-msg">
        {{ error }}
      </p>
    </div>

    <label>代理商</label>
    <input v-model.trim="RegAgent" type="text" disabled style="color: deeppink;" />
    
    <div class="label-container">
      <label>用戶帳號</label>
      <p v-if="isCheckingAccount && RegiestAccount" class="field-msg" style="color: yellow;">
        正在檢查帳號...
      </p>
      <p v-if="RegiestAccount && userStore.errorMessageRegiedt" class="field-msg" :style="{ color: userStore.isAccOk ? 'lightgreen' : 'red' }">
        {{ userStore.errorMessageRegiedt }}
      </p>
      <span v-if="accountError" class="label-error">{{ accountError }}</span>
    </div>
    <input
      v-model.trim="RegiestAccount"
      type="text"
      placeholder="請輸入電子信箱"
      @blur="doCheckAcc"
    />

    <div class="label-container">
      <label>用戶簡稱</label>
      <span v-if="nicknameError" class="label-error">{{ nicknameError }}</span>
    </div>    
    <input v-model.trim="RegiestName" type="text" placeholder="暱稱" @blur="validateNickname" />

    <label>統一編號</label>
    <input v-model.trim="RegiestCompanyNo" type="text" placeholder="公司請輸入個人用戶不用輸入" />

    <label>電話</label>
    <input v-model.trim="RegiestCellPhone" type="text" placeholder="行動電話或室內電話或公司電話" />

    <label>縣/市</label>
    <select v-model="RegCity" @change="onCityChange">
      <option value="">請選擇城市</option>
      <option v-for="district in sortedDistricts" :key="district" :value="district">{{ district }}</option>
    </select>

    <label>郵遞區號</label>
    <input v-model.trim="RegZipCode" type="text" placeholder="自動填入郵遞區號" disabled />

    <label>收件地址</label>
    <input v-model.trim="RegAddress" type="text" placeholder="請輸入地址" />

    <label>密碼 (至少8個字符、包含大小寫、數字、符號)</label>
    <div class="password-wrapper">
      <input
        v-model.trim="RegiestPw1"
        :type="showPw1 ? 'text' : 'password'"
        placeholder="密碼請使用大小寫加上符號最少長度不低於8"
      />
      <span class="toggle-text" @click="showPw1 = !showPw1">
        {{ showPw1 ? '隱藏' : '顯示' }}
      </span>
    </div>
    <p v-if="RegiestPw1" class="field-msg" :style="{ color: passwordStrength.color }">
      密碼強度：{{ passwordStrength.label }}
    </p>

    <label>密碼驗證</label>
    <div class="password-wrapper">
      <input 
        v-model.trim="RegiestPw2" 
        :type="showPw2 ? 'text' : 'password'" 
        placeholder="重複密碼" 
      />
      <span class="toggle-text" @click="showPw2 = !showPw2">
        {{ showPw2 ? '隱藏' : '顯示' }}
      </span>
    </div>

    <button :disabled="!RegBtnenable" @click="DoRegiest">申請註冊</button>
    <button @click="showLogin">返回</button>
  </div>
</template>

<style>
.dRegiest {
  max-width: 450px;
  border-radius: 10px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: black;
  border: 2px solid #ff1493;
  padding: 20px;
}

.label-container {
  display: flex;
  align-items: center;
  width: 400px;
  margin: 10px 10px 0 10px;
}

label {
  text-align: left;
  font-size: 18px;
  color: white;
}

.label-error {
  font-size: 14px;
  color: red;
  font-weight: bold;
  margin-left: 10px;
}

input,
select {
  border-radius: 10px;
  background-color: black;
  color: white;
  font-size: 18px;
  margin: 10px;
  width: 400px;
  padding: 5px;
  border: 1px solid #ff1493;
}

input:disabled {
  color: deeppink;
  opacity: 0.7;
}

button {
  margin: 15px 10px 0 10px;
  border-radius: 10px;
  border: none;
  color: white;
  font-size: 20px;
  background-color: deeppink;
  width: 400px;
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  align-self: center;
}

button:disabled {
  background-color: grey;
  cursor: not-allowed;
}

button:hover:not(:disabled) {
  transform: scale(1.04);
}

.field-msg {
  margin: -5px 10px 10px;
  font-size: 14px;
  font-weight: bold;
}

.error-summary {
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid red;
  border-radius: 5px;
  background-color: rgba(255, 0, 0, 0.1);
  width: 100%;
}

/* --- 密碼切換專屬樣式 --- */
.password-wrapper {
  position: relative;
  width: 400px;
  margin: 10px;
}

.password-wrapper input {
  width: 100%;
  margin: 0;
  padding-right: 55px; /* 為右側文字預留空間 */
  box-sizing: border-box;
}

.password-wrapper .toggle-text {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #ff1493;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.password-wrapper .toggle-text:hover {
  color: #ff69b4;
}
</style>